/**
 * NFC Plugin - Bridge between Capacitor native NFC and JavaScript
 *
 * This module provides a stable, singleton-based NFC interface that:
 * - Manages NFC read/write operations
 * - Handles background NFC tag detection
 * - Processes pending intents from cold start
 * - Provides consistent error handling and state management
 */

import { nfcPluginLogger as log } from './nfc-logger';
import type {
  NfcInternalPlugin,
  NfcPlugin,
  NfcReadEvent,
  NfcWriteEvent,
} from './nfc-plugin.definitions';
import {
  decodeTagDetectedEvent,
  formatWrittenPayload,
} from './nfc-plugin.utils';

import { App } from '@capacitor/app';
import { Capacitor, registerPlugin } from '@capacitor/core';

// ============================================================================
// Internal Plugin Registration
// ============================================================================

const nfcInternalPlugin = registerPlugin<NfcInternalPlugin>('Nfc', {
  web: () => ({
    async nfcStatus() {
      // Web doesn't support NFC
      return { available: false, enabled: false };
    },
    async openNfcSettings() {
      // No-op on web
    },
    async startReading() {
      // No-op on web
    },
    async writeTag() {
      // No-op on web
    },
    async cancelRead() {
      // No-op on web
    },
    async cancelWrite() {
      // No-op on web
    },
    async checkPendingIntent() {
      // No-op on web
    },
    async addListener() {
      return { remove: async () => {} };
    },
    async removeAllListeners() {
      // No-op on web
    },
  }),
});

// ============================================================================
// NFC State Management
// ============================================================================

// GearTracker domains for URL detection
const GT_DOMAINS: string[] = [
  'app.geartracker.net',
  'staging.geartracker.pages.dev',
];

/**
 * Check if a URL belongs to GearTracker
 */
const isGearTrackerUrl = (url: string): boolean => {
  return GT_DOMAINS.some((domain) => url.includes(domain));
};

// ============================================================================
// NFC Plugin Singleton
// ============================================================================

export const Nfc: NfcPlugin = {
  // Event listeners for background NFC detection
  listeners: {
    emptyTag: [],
    gearTrackerTag: [],
    otherTag: [],
  },

  // Promise handlers for active read/write operations
  readPromise: {
    resolve: () => {},
    reject: () => {},
  },
  writePromise: {
    resolve: () => {},
    reject: () => {},
  },

  // Current state - prevents concurrent operations
  isBusy: false,

  /**
   * Cancel an active NFC read operation
   */
  cancelRead: async () => {
    log.info('Cancelling read operation', { wasBusy: Nfc.isBusy });

    try {
      await nfcInternalPlugin.cancelRead();
    } catch (error) {
      log.warn('Error during native cancelRead', error);
    }

    if (Nfc.isBusy) {
      Nfc.isBusy = false;
      Nfc.readPromise.reject({ error: 'Aborted' });
    }
  },

  /**
   * Cancel an active NFC write operation
   */
  cancelWrite: async () => {
    log.info('Cancelling write operation', { wasBusy: Nfc.isBusy });

    try {
      await nfcInternalPlugin.cancelWrite();
    } catch (error) {
      log.warn('Error during native cancelWrite', error);
    }

    if (Nfc.isBusy) {
      Nfc.isBusy = false;
      Nfc.writePromise.reject({ error: 'Aborted' });
    }
  },

  /**
   * Check NFC hardware availability and enabled state
   */
  nfcStatus: nfcInternalPlugin.nfcStatus,

  /**
   * Open device NFC settings
   */
  openNfcSettings: nfcInternalPlugin.openNfcSettings,

  /**
   * Start an NFC read operation
   *
   * On iOS: Opens the native NFC scanning dialog
   * On Android: Enables foreground dispatch for NFC intents
   *
   * @returns Promise that resolves when a tag is detected
   * @throws Error if another operation is in progress
   */
  read: async (): Promise<NfcReadEvent> => {
    log.info('Read requested', { currentState: Nfc.isBusy ? 'busy' : 'idle' });

    if (Nfc.isBusy) {
      log.warn('Read rejected - operation already in progress');
      throw new Error('An action is already in progress');
    }

    Nfc.isBusy = true;
    log.debug('State changed to busy');

    try {
      // Start native NFC reading session
      log.debug('Starting native NFC reading session');
      await nfcInternalPlugin.startReading();
      log.debug('Native reading session started, waiting for tag');
    } catch (error) {
      log.error('Failed to start reading session', error);
      Nfc.isBusy = false;
      throw error;
    }

    return new Promise<NfcReadEvent>((resolve, reject) => {
      Nfc.readPromise = { resolve, reject };
    });
  },

  /**
   * Write NDEF records to an NFC tag
   *
   * @param message - NDEF message containing records to write
   * @returns Promise that resolves when write is complete
   * @throws Error if another operation is in progress or no records provided
   */
  write: async (message): Promise<NfcWriteEvent> => {
    log.info('Write requested', {
      recordCount: message.records.length,
      currentState: Nfc.isBusy ? 'busy' : 'idle',
    });

    if (Nfc.isBusy) {
      log.warn('Write rejected - operation already in progress');
      throw new Error('An action is already in progress');
    }

    if (message.records.length === 0) {
      log.warn('Write rejected - no records provided');
      throw new Error('At least one NDEF record is required');
    }

    Nfc.isBusy = true;
    log.debug('State changed to busy');

    try {
      const formattedMessage = formatWrittenPayload(message);
      log.debug('Formatted message for native write', {
        recordCount: formattedMessage.records.length,
      });

      await nfcInternalPlugin.writeTag(formattedMessage);
      log.debug('Native write started, waiting for completion');
    } catch (error) {
      log.error('Failed to start write operation', error);
      Nfc.isBusy = false;
      throw error;
    }

    return new Promise<NfcWriteEvent>((resolve, reject) => {
      Nfc.writePromise = { resolve, reject };
    });
  },

  /**
   * Register a listener for background NFC events
   *
   * @param eventName - Type of event to listen for
   * @param listenerFunc - Callback function
   * @returns Handle to remove the listener
   */
  on: (eventName, listenerFunc) => {
    log.debug('Registering listener', { eventName });

    switch (eventName) {
      case 'emptyTag':
        Nfc.listeners.emptyTag.push(listenerFunc);
        return Promise.resolve({
          remove: async () => {
            Nfc.listeners.emptyTag = Nfc.listeners.emptyTag.filter(
              (l) => l !== listenerFunc,
            );
            log.debug('Removed emptyTag listener');
          },
        });

      case 'gearTrackerTag':
        Nfc.listeners.gearTrackerTag.push(listenerFunc);
        return Promise.resolve({
          remove: async () => {
            Nfc.listeners.gearTrackerTag = Nfc.listeners.gearTrackerTag.filter(
              (l) => l !== listenerFunc,
            );
            log.debug('Removed gearTrackerTag listener');
          },
        });

      case 'otherTag':
        Nfc.listeners.otherTag.push(listenerFunc);
        return Promise.resolve({
          remove: async () => {
            Nfc.listeners.otherTag = Nfc.listeners.otherTag.filter(
              (l) => l !== listenerFunc,
            );
            log.debug('Removed otherTag listener');
          },
        });
    }
  },

  /**
   * Signal that the app is ready to receive pending NFC intents
   *
   * Should be called after all listeners are registered to handle
   * NFC intents that arrived before the app was fully initialized
   */
  setAppAsReady: () => {
    log.info('App marked as ready, checking for pending intents');

    nfcInternalPlugin.checkPendingIntent().catch((error) => {
      log.error('Failed to check pending NFC intent', error);
    });
  },

  /**
   * Enable background NFC listening (Android only)
   *
   * This activates the foreground dispatch so NFC tags can be detected
   * even when not in the scanner view. Tags will be processed as
   * background events (emptyTag, gearTrackerTag, otherTag).
   *
   * On iOS, this is a no-op as iOS requires explicit user action to scan.
   */
  enableBackgroundListening: () => {
    if (Capacitor.getPlatform() !== 'android') {
      log.debug('Background listening only available on Android');
      return;
    }

    if (Nfc.isBusy) {
      log.debug('Already in active read mode, skipping background listening');
      return;
    }

    log.info('Enabling background NFC listening');

    // Call startReading to activate foreground dispatch
    // but don't set isBusy - tags will be processed as background events
    nfcInternalPlugin.startReading().catch((error) => {
      log.warn('Failed to enable background listening', error);
    });
  },

  /**
   * Disable background NFC listening (Android only)
   */
  disableBackgroundListening: () => {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    if (Nfc.isBusy) {
      log.debug('In active read mode, not disabling background listening');
      return;
    }

    log.info('Disabling background NFC listening');
    nfcInternalPlugin.cancelRead().catch(() => {});
  },
};

// ============================================================================
// Native Event Handlers
// ============================================================================

/**
 * Handle NFC tag detection from native plugin
 */
nfcInternalPlugin.addListener('nfcTagDetected', (event) => {
  log.info('Tag detected', {
    serialNumber: event.serialNumber,
    recordCount: event.message.records.length,
    isBusy: Nfc.isBusy,
  });

  const decodedEvent = decodeTagDetectedEvent(event);

  // If we're in reading mode, resolve the read promise
  if (Nfc.isBusy) {
    log.info('Resolving active read operation');
    Nfc.isBusy = false;
    Nfc.readPromise.resolve(decodedEvent);
    return;
  }

  // Background event handling
  log.debug('Processing as background event');

  // Empty tag case
  if (decodedEvent.message.records.length === 0) {
    log.info('Empty tag detected, notifying listeners', {
      listenerCount: Nfc.listeners.emptyTag.length,
    });
    for (const listener of Nfc.listeners.emptyTag) {
      listener({ serialNumber: decodedEvent.serialNumber });
    }
    return;
  }

  // Check for GearTracker URL in records
  const urlRecords = decodedEvent.message.records.filter(
    (record) => record.type === 'U',
  );

  let gearTrackerUrl: string | undefined;
  for (const urlRecord of urlRecords) {
    if (isGearTrackerUrl(urlRecord.payload)) {
      gearTrackerUrl = urlRecord.payload;
      break;
    }
  }

  if (gearTrackerUrl) {
    log.info('GearTracker tag detected', {
      url: gearTrackerUrl,
      listenerCount: Nfc.listeners.gearTrackerTag.length,
    });
    for (const listener of Nfc.listeners.gearTrackerTag) {
      listener({
        serialNumber: decodedEvent.serialNumber,
        url: gearTrackerUrl,
      });
    }
    return;
  }

  // Other tag case
  log.info('Other tag detected (background)', {
    listenerCount: Nfc.listeners.otherTag.length,
    serialNumber: decodedEvent.serialNumber,
  });

  if (Nfc.listeners.otherTag.length === 0) {
    log.warn('No otherTag listeners registered - tag will be ignored');
  }

  for (const listener of Nfc.listeners.otherTag) {
    log.debug('Calling otherTag listener');
    listener(decodedEvent);
  }
});

/**
 * Handle URL detection from NFC intent (Android ACTION_VIEW)
 */
nfcInternalPlugin.addListener('nfcUrlDetected', (event) => {
  log.info('URL detected from intent', { url: event.url });

  if (isGearTrackerUrl(event.url)) {
    log.info('GearTracker URL, notifying listeners', {
      listenerCount: Nfc.listeners.gearTrackerTag.length,
    });
    for (const listener of Nfc.listeners.gearTrackerTag) {
      listener(event);
    }
  } else {
    log.debug('Non-GearTracker URL, ignoring');
  }
});

/**
 * Handle write completion from native plugin
 */
nfcInternalPlugin.addListener('onWrite', (event) => {
  log.info('Write event received', {
    success: event.success,
    serialNumber: event.serialNumber,
    error: event.error,
    isBusy: Nfc.isBusy,
  });

  if (!Nfc.isBusy) {
    log.warn('Write event ignored - not in busy state');
    return;
  }

  Nfc.isBusy = false;

  if (event.success) {
    log.info('Write successful');
    Nfc.writePromise.resolve({ serialNumber: event.serialNumber });
  } else {
    log.error('Write failed', { error: event.error });
    Nfc.writePromise.reject({
      serialNumber: event.serialNumber,
      error: event.error ?? 'Unknown write error',
    });
  }
});

/**
 * Handle NFC session cancellation (iOS only)
 */
nfcInternalPlugin.addListener('onCancel', (event) => {
  log.info('Session cancelled', { reason: event.reason, isBusy: Nfc.isBusy });

  if (!Nfc.isBusy) {
    log.debug('Cancel event ignored - not in busy state');
    return;
  }

  Nfc.isBusy = false;

  // Map reason to error message
  const error = event.reason === 'user_cancelled' ? 'Cancelled' : event.reason;

  log.info('Rejecting pending operations', { error });
  Nfc.readPromise.reject({ error });
  Nfc.writePromise.reject({ error });
});

// ============================================================================
// App Lifecycle Handlers
// ============================================================================

/**
 * Reset NFC state when app lifecycle changes
 * This handles cases where:
 * - iOS terminates NFC session when app goes to background
 * - Android disables foreground dispatch in background
 * - User force-quits and restarts the app
 */
const resetNfcState = () => {
  if (Nfc.isBusy) {
    log.info('Resetting NFC state (was busy)');
    Nfc.isBusy = false;
    // Reject any pending promises with abort error
    Nfc.readPromise.reject({ error: 'Aborted' });
    Nfc.writePromise.reject({ error: 'Aborted' });
  }
};

// Set up lifecycle listeners on native platforms
if (Capacitor.isNativePlatform()) {
  // Handle app going to background - cancel any active NFC operations
  // NOTE: Both iOS and Android can trigger false "inactive" states when
  // NFC modals or other system dialogs are shown. If an NFC operation is
  // in progress (isBusy=true), we assume it's a false positive and ignore it.
  App.addListener('appStateChange', (state) => {
    if (!state.isActive) {
      if (Nfc.isBusy) {
        log.debug(
          'App state changed to inactive but NFC operation in progress, ignoring',
        );
        return;
      }

      log.info('App going to background, cancelling NFC operations');
      // Cancel native operations
      nfcInternalPlugin.cancelRead().catch(() => {});
      nfcInternalPlugin.cancelWrite().catch(() => {});
      // Reset JavaScript state
      resetNfcState();
    } else {
      log.debug('App resumed to foreground');
    }
  });

  log.debug('App lifecycle listeners registered for NFC');
}

// Log initialization
log.info('NFC plugin initialized', {
  platform: Capacitor.getPlatform(),
  isNative: Capacitor.isNativePlatform(),
});
